import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import {
  AnnouncementDialog,
  AnnouncementDialogData,
} from '@app/features/announcements/announcement-dialog/announcement-dialog';
import {
  announcementDirectionSections,
  announcementSubtitle,
} from '@app/features/announcements/announcement-presentation';
import { Announcement } from '@app/features/announcements/announcement.model';
import { AnnouncementService } from '@app/features/announcements/announcement.service';
import { ANOMALY_AUTHORS } from '@app/features/anomalies/anomaly.model';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import {
  LucideArchive,
  LucideCompass,
  LucideExternalLink,
  LucidePencil,
  LucideSparkles,
} from '@lucide/angular';
import { DEMO_PATH_PREFIX, DEMO_TOUR_ID } from '../demo/demo-mode';
import { TourService } from '../tour.service';
import { TOURS, toursByDomain } from '../tours';

interface AnnouncementItem {
  id: string;
  title: string;
  subtitle: string;
  announcement: Announcement;
}

/**
 * L'icône « Parcours » de la barre de navigation : les parcours de découverte, par domaine, que
 * tout utilisateur connecté relance quand il veut, chacun dans un onglet de démonstration. La
 * direction y annonce aussi les nouveautés, à côté du catalogue dont elle choisit le parcours, et
 * relit ses annonces (RG-nouveaute-annonce, décision 2026-032/15).
 */
@Component({
  selector: 'app-menu-tours',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    MatButtonModule,
    MatDividerModule,
    MatMenuModule,
    MatTooltip,
    LucideArchive,
    LucideCompass,
    LucideExternalLink,
    LucidePencil,
    LucideSparkles,
  ],
  templateUrl: './menu-tours.html',
  styleUrls: ['./menu-tours.scss'],
})
export class MenuTours {
  private readonly tours = inject(TourService);
  private readonly announcementService = inject(AnnouncementService);
  private readonly dialog = inject(MatDialog);
  protected readonly demoPathPrefix = DEMO_PATH_PREFIX;

  /**
   * Seule la direction annonce (RG-anomalie-saisie-par-la-direction), et jamais depuis un onglet
   * de démonstration, qui n'appelle pas l'API.
   */
  protected readonly isDirection =
    inject(AUTHENTICATED_USER_SERVICE).hasAnyRole(ANOMALY_AUTHORS) &&
    !inject(DEMO_TOUR_ID, { optional: true });

  /** Seulement les parcours dont l'utilisateur a le droit de voir au moins une étape. */
  protected readonly domains = computed(() =>
    toursByDomain(TOURS.filter((tour) => this.tours.visibleSteps(tour).length > 0)),
  );

  private readonly announcements = toSignal(this.announcementService.all$, { initialValue: [] });

  /** Les nouveautés de la direction : affichées, programmées, terminées, puis archivées à part. */
  protected readonly announcementItems = computed(() => {
    const sections = announcementDirectionSections(this.announcements());
    const items = (announcements: Announcement[]): AnnouncementItem[] =>
      announcements.map((announcement) => ({
        id: announcement.id,
        title: announcement.title,
        subtitle: announcementSubtitle(announcement),
        announcement,
      }));
    return {
      current: items([...sections.displayed, ...sections.scheduled, ...sections.ended]),
      archived: items(sections.archived),
    };
  });

  protected openAnnouncementDialog(announcement?: Announcement): void {
    this.dialog.open<AnnouncementDialog, AnnouncementDialogData>(AnnouncementDialog, {
      width: '80%',
      minWidth: '320px',
      maxWidth: '760px',
      disableClose: true,
      data: announcement ? { announcement } : {},
    });
  }
}
